'use client'

import { useCallback, useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'

import { extractCustomerFromClientSession } from 'modules/customer'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import { reportPostalCodeCheckResult } from '../analytics'
import { useDeliverySupport } from '../providers/DeliverySupportContext'

export const useCheckDeliveryCoverage = (open: boolean) => {
  const { postalCodeAndCityContext, validateAndUpdateAddress } =
    useDeliverySupport()
  const session = useSession()
  const user = extractCustomerFromClientSession(session)

  const [isDeliveryPossible, setIsDeliveryPossible] = useState<boolean | null>(
    null
  )

  useEffect(() => {
    if (!open) {
      setIsDeliveryPossible(null)

      return
    }

    if (isNotNilOrEmpty(postalCodeAndCityContext?.postalCode)) {
      setIsDeliveryPossible(postalCodeAndCityContext.isSupported)
    }
  }, [open, postalCodeAndCityContext])

  const update = useCallback(
    async ({ postalCode, city }: { postalCode: string; city: string }) => {
      const response = await validateAndUpdateAddress({
        postalCode: postalCode,
        city: city
      })

      setIsDeliveryPossible(response.isSupported ?? false)

      reportPostalCodeCheckResult({
        customer: user,
        zipCode: postalCode,
        isAvailable: response.isSupported
      })

      if (!response.isSupported) {
        return
      }

      return response
    },
    [validateAndUpdateAddress, user]
  )

  const clearDeliveryPossible = useCallback(() => {
    setIsDeliveryPossible(null)
  }, [])

  return {
    isDeliveryPossible,
    update,
    postalCodeAndCityContext,
    clearDeliveryPossible
  }
}
